import React from "react";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  ThemeProvider,
  CssBaseline,
} from "@mui/material";
import { useParams } from "react-router-dom";
import ClientRecipeContent from "./components/ClientRecipeContent";
import { createClientTheme } from "../client-meal-plan/theme/ClientTheme";
import { usePublicRecipe } from "../../hooks/usePublicRecipe";

/**
 * ClientRecipeViewer - Public-facing recipe viewer for clients
 * This component loads a recipe by ID and displays it in a client-friendly format
 * No authentication required - designed for sharing recipes publicly
 */
const ClientRecipeViewer = () => {
  const { organizationId, recipeId } = useParams();

  // Use the new API hook to fetch public recipe data
  const {
    data: recipeData,
    isLoading,
    error: apiError,
  } = usePublicRecipe(organizationId, recipeId);

  // Extract data from API response
  const recipe = recipeData?.recipe;
  const organizationBranding = recipeData?.organizationBranding || null;

  // Check if we're still loading
  const loading = isLoading || !organizationId || !recipeId;

  // Format error message
  const error = apiError ? apiError.message : null;

  // Create custom theme with organization branding from API
  const clientTheme = createClientTheme(organizationBranding);

  // Show loading state if hooks are still loading or data is not available
  if (loading) {
    return (
      <ThemeProvider theme={clientTheme}>
        <CssBaseline />
        <Container>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100vh"
            flexDirection="column"
          >
            <CircularProgress />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading recipe...
            </Typography>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  if (error) {
    // Determine specific error message based on error content
    let errorTitle = "Recipe Unavailable";
    let errorMessage = error;
    let helpText = [
      "Check that the link is correct",
      "Ensure the recipe is set to public",
      "Contact support if the problem persists",
    ];

    // Check if this is a 403 forbidden error (not public)
    if (error.includes("not publicly accessible")) {
      errorTitle = "Recipe Not Public";
      errorMessage =
        "This recipe is not publicly accessible. Only public recipes can be viewed through this link.";
    } else if (error.includes("not found")) {
      errorTitle = "Recipe Not Found";
      errorMessage =
        "This recipe was not found. It may have been deleted or the link is incorrect.";
      helpText = [
        "Check that the link is correct",
        "Verify the recipe hasn't been deleted",
        "Contact the organization if you believe this is an error",
      ];
    } else if (error.includes("Organization not found")) {
      errorTitle = "Organization Not Found";
      errorMessage =
        "The organization associated with this recipe could not be found.";
      helpText = [
        "Check that the link is correct",
        "Verify the organization is still active",
        "Contact support if the problem persists",
      ];
    }

    return (
      <ThemeProvider theme={clientTheme}>
        <CssBaseline />
        <Container>
          <Box sx={{ py: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {errorTitle}
              </Typography>
              <Typography variant="body2">{errorMessage}</Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  If you believe this is an error, please:
                </Typography>
                <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                  {helpText.map((text, index) => (
                    <Typography
                      key={index}
                      component="li"
                      variant="body2"
                      sx={{ mb: 0.5 }}
                    >
                      {text}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Alert>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={clientTheme}>
      <CssBaseline />
      <ClientRecipeContent
        recipe={recipe}
        organizationBranding={organizationBranding}
        organizationId={organizationId}
        recipeId={recipeId}
      />
    </ThemeProvider>
  );
};

export default ClientRecipeViewer;
